import { useState, useEffect, useRef } from "react";
import { Layout } from "../../global/Layout";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import { Store } from "../../../types";
import { getStoreById } from "../../../lib/admin";
import NoImage from "../../../assets/NoImage.jpg";
import { SpinnerCircularFixed } from "../../../assets/spinner/SpinnerCircularFixed";

import StoreSyncForm from "./StoreSyncForm";
import StoreRetailSystemForm from "./StoreRetailSystemForm";
import StoreShopifyForm from "./StoreShopifyForm";
import StoreCSVForm from "./StoreCSVForm";
import StoreSyncFormType from "./StoreSyncTypeForm";
import StoreShopifySettings from "./StoreShopifySettings";
import DecommissionTab from "./decommission/DecommissionTab";
import StoreActivityTab from "../../logs/StoreActivityTab";
import StoreReportTab from "../report/StoreReportTab";
import StoreCustomizationContainer from "../../customization/StoreCustomizationContainer";
import {
  UnsavedChangesProvider,
  useUnsavedChanges,
} from "../../customization/hooks/useUnsavedChangesGuard";
import ConfirmModal from "../../global/ConfirmModal";

import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

import { useAuth } from "../../../utils/authorization";
import { useHasAllStoresAccess, useHasScope } from "../../../utils/Permission";
import CONSTANTS from "../../../lib/constants";
import { BackButton } from "../../../assets/BackButton";

type TabKey =
  | "syncSettings"
  | "syncType"
  | "retailSystem"
  | "shopify"
  | "csv"
  | "settings"
  | "customization"
  | "decommission"
  | "activity"
  | "report";

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

function TabPanel({
  children,
  activeKey,
  tabKey,
  noPadding = false,
}: {
  children?: React.ReactNode;
  activeKey: TabKey;
  tabKey: TabKey;
  noPadding?: boolean;
}) {
  if (activeKey !== tabKey) return null;
  return (
    <Box sx={{ p: noPadding ? 0 : 3, width: "100%", maxWidth: "100%" }}>
      <Typography component="div">{children}</Typography>
    </Box>
  );
}

const TAB_CLASS: Record<TabKey, string> = {
  syncSettings: "SyncTab",
  syncType: "SyncTypeTab",
  retailSystem: "RetailSystemTab",
  shopify: "ShopifyTab",
  csv: "CSVTab",
  settings: "SettingsTab",
  customization: "CustomizationToolTab",
  decommission: "DecommissionTab",
  activity: "ActivityTab",
  report: "ReportTab",
};

export default function StoreManagementContainer() {
  return (
    <UnsavedChangesProvider>
      <StoreManagementContent />
    </UnsavedChangesProvider>
  );
}

function StoreManagementContent() {
  const { storeId } = useParams<{ storeId: string }>();
  const [store, setStore] = useState<Store | undefined>(undefined);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasBeenUpdated, setHasBeenUpdated] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const pendingAction = useRef<() => void>(() => { });
  const [firstLoad, setFirstLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [openMobileMenu, setOpenMobileMenu] = useState(false);
  const [tabsCollapsed, setTabsCollapsed] = useState(() => localStorage.getItem("management-tabs-collapsed") === "true");
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const { user } = useAuth();
  const isLoggedIn = !!user;
  const hasAllStoresAccess = useHasAllStoresAccess();

  const { runWithConfirmation } = useUnsavedChanges();

  const hasUpdateStoreSync = useHasScope("update:storesyncsettings") || user?.stores?.length === 1;
  const hasUpdateSyncType = useHasScope("update:synctype");
  const hasUpdateShopifyInfo = useHasScope(Object.values(CONSTANTS.SHOPIFY_INFO_SCOPES), true);
  const hasUpdateRSInformation = useHasScope("update:rsinformation");
  const hasUpdateJsonSettings = useHasScope("update:jsonsettings");
  const hasUpdateCsvConfiguration = useHasScope("update:csvconfiguration");
  const hasDeleteStore = useHasScope(CONSTANTS.DELETE_STORE_SCOPES);
  const hasViewLogs = useHasScope("logs:view");

  const toggleTabsCollapsed = () => {
    const next = !tabsCollapsed;
    setTabsCollapsed(next);
    localStorage.setItem("management-tabs-collapsed", String(next));
  };

  const toggleMenu = (state: boolean) => () => {
    setOpenMobileMenu(state);
  };

  const availableTabs: { key: TabKey; label: string }[] = [
    ...(hasUpdateStoreSync ? [{ key: "syncSettings" as const, label: "Sync Settings" }] : []),
    ...(hasUpdateSyncType ? [{ key: "syncType" as const, label: "Sync Type" }] : []),
    ...(hasUpdateRSInformation ? [{ key: "retailSystem" as const, label: "RetailSystem" }] : []),
    ...(hasUpdateShopifyInfo ? [{ key: "shopify" as const, label: "Shopify" }] : []),
    ...(hasUpdateCsvConfiguration ? [{ key: "csv" as const, label: "CSV" }] : []),
    ...(hasUpdateJsonSettings ? [{ key: "settings" as const, label: "Settings" }] : []),
    { key: "customization" as const, label: "Customization" },
    ...(hasDeleteStore ? [{ key: "decommission" as const, label: "Delete Store" }] : []),
    ...(hasViewLogs ? [{ key: "activity" as const, label: "Activity" }] : []),
    { key: "report" as const, label: "Report" },
  ];

  const [activeTab, setActiveTab] = useState<TabKey>(availableTabs[0].key);

  const updateStore = async () => {
    if (!storeId) {
      setStore(undefined);
      setIsLoading(false);
      return;
    }
    if (!store) setIsLoading(true);
    try {
      const newStore = await getStoreById(parseInt(storeId));
      setStore(newStore);
    } catch (error) {
      console.error("Error fetching store:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const qTab = searchParams.get("tab") as TabKey | null;

    if (qTab && availableTabs.some((t) => t.key === qTab)) {
      setActiveTab(qTab);
    }
  }, [searchParams, availableTabs]);

  useEffect(() => {
    if (hasBeenUpdated || firstLoad) {
      updateStore();
      setHasBeenUpdated(false);
      setFirstLoad(false);
    }
  }, [store, hasBeenUpdated]);

  useEffect(() => {
    if (storeId && storeId !== "null") {
      updateStore();
    } else {
      setIsLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const nextFirst = availableTabs[0]?.key ?? "customization";
    setActiveTab((prev) => (availableTabs.some((t) => t.key === prev) ? prev : nextFirst));
  }, [
    hasUpdateStoreSync,
    hasUpdateSyncType,
    hasUpdateShopifyInfo,
    hasUpdateRSInformation,
    hasUpdateJsonSettings,
    hasUpdateCsvConfiguration,
    hasDeleteStore,
    isLoggedIn,
  ]);

  const handleGoBack = () => {
    window.location.href = "/stores";
  };

   const handleNavigateHome = () => {
    if (activeTab === "customization") {
      runWithConfirmation(() => navigate('/'));
    } else {
      navigate('/');
    }
  };

  const handleChange = (_: React.SyntheticEvent, newValue: TabKey | "__back") => {
    if (newValue === "__back") {
      handleGoBack();
      return;
    }

    const allowed = availableTabs.some((t) => t.key === newValue);
    if (!allowed) return;

    const goToTab = () => {
      setActiveTab(newValue);

      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("tab", newValue);

      if (newValue !== "customization") {
        nextParams.delete("section");
      }

      setSearchParams(nextParams, { replace: true });
    };

    leaveWithConfirmation(goToTab);
  };

  const leaveWithConfirmation = (proceed: () => void) => {
    if (activeTab === "customization") {
      runWithConfirmation(proceed);
      return;
    }

    if (hasUnsavedChanges) {
      pendingAction.current = proceed;
      setConfirmDialogOpen(true);
      return;
    }

    proceed();
  };


  const handleConfirmDialogClose = (confirmed: boolean) => {
    setConfirmDialogOpen(false);
    if (confirmed) {
      setHasUnsavedChanges(false);
      pendingAction.current();
    }
  };

  const getLogo: () => string = () => {
    if (store) {
      if (!store.logo) return NoImage;
      const image = new Image();
      image.src = store.logo;
      return image.width > 0 && image.height > 0 ? store.logo : NoImage;
    }
    return NoImage;
  };

  return (
    <>
      <Layout>
        <div className="editStores">

          <div className="mobile-side-menu">
            <IconButton onClick={toggleMenu(true)}>
              <MenuIcon />
            </IconButton>
            {!isLoading && isLoggedIn && hasAllStoresAccess && (
              <button className="backBtn" onClick={handleNavigateHome}><BackButton color={"black"} /> Back</button>
            )}
          </div>

          <Drawer open={openMobileMenu} onClose={toggleMenu(false)}>
            <div className="mobile-drawer-content">
              <button className="backBtn" onClick={() => setOpenMobileMenu(false)} >
                <BackButton color={"black"} />
              </button>
              <div className="logo-title">
                <img
                  src={getLogo()}
                  alt={store?.friendlyName}
                  className="mobile-drawer-logo"
                />

                <h4 className="mobile-drawer-title">{store?.friendlyName}</h4>
              </div>

              {availableTabs.map((t) => (
                <div
                  key={t.key}
                  className={`mobile-tab-item ${TAB_CLASS[t.key]} ${activeTab === t.key ? "active" : ""
                    }`}
                  onClick={() => {
                    handleChange({} as any, t.key);
                    setOpenMobileMenu(false);
                  }}
                >
                  {t.label}
                </div>
              ))}
            </div>
          </Drawer>

          <Box
            sx={{
              flexGrow: 1,
              bgcolor: "background.paper",
              display: "flex",
              borderRadius: "15px",
            }}
          >
            <div className={`leftVertical ${tabsCollapsed ? "collapsed" : ""}`}>
              <Tabs
                orientation="vertical"
                variant="scrollable"
                aria-label="Vertical tabs"
                sx={{ borderRight: 1, borderColor: "divider" }}
                value={activeTab}
                onChange={handleChange}
              >
                {/* {(!stores || (stores.length === 0 || Number(stores[0]) === 0)) && (
                  <Tab className="BackTab" label="Back" onClick={handleGoBack} value="__back" />
                )} */}
                <div className="logo-title-store">
                  <div className="one-col storeLogo">
                    <img
                      src={getLogo()}
                      alt={`logo-${store?.friendlyName}`}
                      id="logo-store"
                      className="logo-store-edit-section"
                    />
                  </div>
                  <div className="title-site">
                    <h6>{store?.friendlyName}</h6>
                  </div>
                </div>

                {availableTabs.map((t, i) => (
                  <Tab
                    key={t.key}
                    className={TAB_CLASS[t.key]}
                    label={t.label}
                    value={t.key}
                    title={tabsCollapsed ? t.label : undefined}
                    {...a11yProps(i + 2)}
                  />
                ))}
              </Tabs>
              <button
                type="button"
                className="collapse-tabs-btn"
                onClick={toggleTabsCollapsed}
                aria-label={tabsCollapsed ? "Expand menu" : "Collapse menu"}
              >
                {tabsCollapsed ? <ChevronRightRoundedIcon fontSize="small" /> : <ChevronLeftRoundedIcon fontSize="small" />}
              </button>
            </div>

            <div className="dataPanel">
              {!isLoading && isLoggedIn && hasAllStoresAccess && (
                <button className="backBtn panel" onClick={handleNavigateHome}><BackButton color={"black"} /> Back</button>
              )}
              <TabPanel activeKey={activeTab} tabKey="syncSettings">
                {isLoading ? (
                  <div className="loading-div">
                    <SpinnerCircularFixed />
                  </div>
                ) : store ? (
                  <StoreSyncForm
                    store={store}
                    hasBeenUpdated={hasBeenUpdated}
                    hasUnsavedChanges={hasUnsavedChanges}
                    setHasBeenUpdated={setHasBeenUpdated}
                    setHasUnsavedChanges={setHasUnsavedChanges}
                  />
                ) : (
                  <p>Invalid store</p>
                )}
              </TabPanel>

              <TabPanel activeKey={activeTab} tabKey="syncType">
                {isLoading ? (
                  <div className="loading-div">
                    <SpinnerCircularFixed />
                  </div>
                ) : store ? (
                  <StoreSyncFormType
                    store={store}
                    hasBeenUpdated={hasBeenUpdated}
                    hasUnsavedChanges={hasUnsavedChanges}
                    setHasBeenUpdated={setHasBeenUpdated}
                    setHasUnsavedChanges={setHasUnsavedChanges}
                  />
                ) : (
                  <p>Invalid store</p>
                )}
              </TabPanel>

              <TabPanel activeKey={activeTab} tabKey="retailSystem">
                {isLoading ? (
                  <div className="loading-div">
                    <SpinnerCircularFixed />
                  </div>
                ) : store ? (
                  <StoreRetailSystemForm
                    store={store}
                    hasBeenUpdated={hasBeenUpdated}
                    hasUnsavedChanges={hasUnsavedChanges}
                    setHasBeenUpdated={setHasBeenUpdated}
                    setHasUnsavedChanges={setHasUnsavedChanges}
                  />
                ) : (
                  <p>Invalid store</p>
                )}
              </TabPanel>

              <TabPanel activeKey={activeTab} tabKey="shopify">
                {isLoading ? (
                  <div className="loading-div">
                    <SpinnerCircularFixed />
                  </div>
                ) : store ? (
                  <StoreShopifyForm
                    store={store}
                    hasBeenUpdated={hasBeenUpdated}
                    hasUnsavedChanges={hasUnsavedChanges}
                    setHasBeenUpdated={setHasBeenUpdated}
                    setHasUnsavedChanges={setHasUnsavedChanges}
                  />
                ) : (
                  <p>Invalid store</p>
                )}
              </TabPanel>

              <TabPanel activeKey={activeTab} tabKey="csv">
                {isLoading ? (
                  <div className="loading-div">
                    <SpinnerCircularFixed />
                  </div>
                ) : store ? (
                  <StoreCSVForm
                    store={store}
                    hasBeenUpdated={hasBeenUpdated}
                    hasUnsavedChanges={hasUnsavedChanges}
                    setHasBeenUpdated={setHasBeenUpdated}
                    setHasUnsavedChanges={setHasUnsavedChanges}
                  />
                ) : (
                  <p>Invalid store</p>
                )}
              </TabPanel>

              <TabPanel activeKey={activeTab} tabKey="settings">
                {store ? (
                  <StoreShopifySettings
                    store={store}
                    hasBeenUpdated={hasBeenUpdated}
                    hasUnsavedChanges={hasUnsavedChanges}
                    setHasBeenUpdated={setHasBeenUpdated}
                    setHasUnsavedChanges={setHasUnsavedChanges}
                  />
                ) : (
                  <p>Invalid store</p>
                )}
              </TabPanel>

              <TabPanel activeKey={activeTab} tabKey="decommission">
                {isLoading ? (
                  <div className="loading-div">
                    <SpinnerCircularFixed />
                  </div>
                ) : store ? (
                  <DecommissionTab store={store} onStoreChange={() => setHasBeenUpdated(true)} />
                ) : (
                  <p>Invalid store</p>
                )}
              </TabPanel>

              <TabPanel activeKey={activeTab} tabKey="activity">
                {isLoading ? (
                  <div className="loading-div">
                    <SpinnerCircularFixed />
                  </div>
                ) : store ? (
                  <StoreActivityTab store={store} />
                ) : (
                  <p>Invalid store</p>
                )}
              </TabPanel>

              <TabPanel activeKey={activeTab} tabKey="report">
                {isLoading ? (
                  <div className="loading-div">
                    <SpinnerCircularFixed />
                  </div>
                ) : store ? (
                  <StoreReportTab store={store} />
                ) : (
                  <p>Invalid store</p>
                )}
              </TabPanel>

              <TabPanel activeKey={activeTab} tabKey="customization" noPadding>
                {store ? (
                  <StoreCustomizationContainer storeId={store.id.toString()} storeName={store.friendlyName} initialTab={searchParams.get("section") || undefined} />
                ) : isLoading ? (
                  <div className="loading-div">
                    <SpinnerCircularFixed />
                  </div>
                ) : (
                  <p>Invalid store</p>
                )}
              </TabPanel>
            </div>
          </Box>
        </div>
      </Layout>

      <ConfirmModal
        open={confirmDialogOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave without saving?"
        onCancel={() => handleConfirmDialogClose(false)}
        onConfirm={() => handleConfirmDialogClose(true)}
        cancelLabel="Keep editing"
        confirmLabel="Leave"
        confirmColor="primary"
      />
    </>
  );
}